
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Palette, Type, Heart, MessageCircle, Target, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FooterSection from '@/components/homepage/FooterSection';
import { WabisabiIcon } from '@/components/common/WabisabiIcon';

const BrandPlaybook = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F5F2] via-white to-[#F7F5F2]">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#6C4F77] hover:text-[#F4978E]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/wabisabi-logo.png" 
              alt="Wabisabi Logo" 
              className="h-16 w-16" 
            />
            <span className="ml-4 text-3xl font-bold text-[#6C4F77]">Wabisabi</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#6C4F77] mb-4">
            Brand Playbook
          </h1>
          <p className="text-xl text-[#2C2C2C]/80 max-w-2xl mx-auto">
            A comprehensive guide to the Wabisabi brand identity, voice, and visual expression
          </p>
        </div>

        {/* Brand Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
              <Heart className="h-6 w-6 text-[#F4978E]" />
              Brand Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Mission</h3>
              <p className="text-[#2C2C2C]/80">
                Help people remember what matters most in their relationships through thoughtful technology that celebrates the beauty of imperfection and mindful connection.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Vision</h3>
              <p className="text-[#2C2C2C]/80">
                A world where no meaningful moment or person is forgotten, where relationships are nurtured through intentional remembering.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Brand Essence</h3>
              <p className="text-[#2C2C2C]/80">
                Wabisabi finds beauty in the imperfect, impermanent moments that make relationships meaningful.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Visual Identity */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
                <Palette className="h-6 w-6 text-[#F4978E]" />
                Color Palette
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#6C4F77] border-2 border-white shadow-sm"></div>
                  <div>
                    <p className="font-medium text-[#2C2C2C]">Wabisabi Plum</p>
                    <p className="text-sm text-[#2C2C2C]/60">#6C4F77</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F4978E] border-2 border-white shadow-sm"></div>
                  <div>
                    <p className="font-medium text-[#2C2C2C]">Warm Coral</p>
                    <p className="text-sm text-[#2C2C2C]/60">#F4978E</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#2C2C2C] border-2 border-white shadow-sm"></div>
                  <div>
                    <p className="font-medium text-[#2C2C2C]">Soft Charcoal</p>
                    <p className="text-sm text-[#2C2C2C]/60">#2C2C2C</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#F7F5F2] border-2 border-gray-200 shadow-sm"></div>
                  <div>
                    <p className="font-medium text-[#2C2C2C]">Off-White</p>
                    <p className="text-sm text-[#2C2C2C]/60">#F7F5F2</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
                <Type className="h-6 w-6 text-[#F4978E]" />
                Typography
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-nunito text-2xl font-bold text-[#6C4F77] mb-2">Nunito</h3>
                <p className="text-[#2C2C2C]/80 text-sm">Primary font for headings and body text</p>
                <p className="font-nunito text-lg text-[#2C2C2C] mt-2">
                  Friendly, approachable, highly readable
                </p>
              </div>
              <div>
                <h3 className="font-sans text-xl font-semibold text-[#6C4F77] mb-2">Inter</h3>
                <p className="text-[#2C2C2C]/80 text-sm">Secondary font for UI elements</p>
                <p className="font-sans text-base text-[#2C2C2C] mt-2">
                  Clean, modern interface typography
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Brand Voice */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
              <MessageCircle className="h-6 w-6 text-[#F4978E]" />
              Brand Voice & Tone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-4">Brand Personality</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F4978E] rounded-full"></span>
                    <span className="text-[#2C2C2C]"><strong>Mindful:</strong> Thoughtful and intentional</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F4978E] rounded-full"></span>
                    <span className="text-[#2C2C2C]"><strong>Warm:</strong> Approachable and caring</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F4978E] rounded-full"></span>
                    <span className="text-[#2C2C2C]"><strong>Genuine:</strong> Authentic about imperfection</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F4978E] rounded-full"></span>
                    <span className="text-[#2C2C2C]"><strong>Supportive:</strong> Empowering relationships</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#F4978E] rounded-full"></span>
                    <span className="text-[#2C2C2C]"><strong>Elegant:</strong> Simple sophistication</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-4">Voice Examples</h3>
                <div className="space-y-4">
                  <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded-r">
                    <p className="text-sm font-medium text-green-800 mb-1">DO Say:</p>
                    <p className="text-sm text-green-700">"Remember what matters most"</p>
                  </div>
                  <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded-r">
                    <p className="text-sm text-green-700">"It's the little things that make all the difference"</p>
                  </div>
                  <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-r">
                    <p className="text-sm font-medium text-red-800 mb-1">DON'T Say:</p>
                    <p className="text-sm text-red-700">"Never forget anything again" (too perfect)</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Messages */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
              <Target className="h-6 w-6 text-[#F4978E]" />
              Key Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-[#6C4F77]/5 to-[#F4978E]/5 rounded-2xl">
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Core Promise</h3>
                <p className="text-[#2C2C2C] font-medium">"Remember what matters most"</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#F4978E]/5 to-[#6C4F77]/5 rounded-2xl">
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Emotional Benefit</h3>
                <p className="text-[#2C2C2C] font-medium">"Never forget the little things"</p>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-[#6C4F77]/5 to-[#F4978E]/5 rounded-2xl">
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-2">Outcome</h3>
                <p className="text-[#2C2C2C] font-medium">"Deeper, more meaningful relationships"</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Target Audience */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#6C4F77]">
              <Users className="h-6 w-6 text-[#F4978E]" />
              Target Audience
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-4 border border-[#6C4F77]/20 rounded-xl">
                <h3 className="font-semibold text-[#6C4F77] mb-2">Primary</h3>
                <p className="text-sm text-[#2C2C2C]/80">
                  Relationship-conscious individuals (25-45) who value deep connections
                </p>
              </div>
              <div className="p-4 border border-[#F4978E]/20 rounded-xl">
                <h3 className="font-semibold text-[#6C4F77] mb-2">Secondary</h3>
                <p className="text-sm text-[#2C2C2C]/80">
                  Busy professionals who struggle to maintain personal relationships
                </p>
              </div>
              <div className="p-4 border border-[#6C4F77]/20 rounded-xl">
                <h3 className="font-semibold text-[#6C4F77] mb-2">Tertiary</h3>
                <p className="text-sm text-[#2C2C2C]/80">
                  Anyone who has felt guilty about forgetting important details about loved ones
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Design Principles */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-[#6C4F77]">Design Principles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-4">Visual Guidelines</h3>
                <ul className="space-y-2 text-[#2C2C2C]/80">
                  <li>• Clean, spacious layouts with plenty of white space</li>
                  <li>• Soft shadows and gentle curves (10-16px radius)</li>
                  <li>• Gradient backgrounds that feel warm and inviting</li>
                  <li>• Purposeful animation that feels natural</li>
                  <li>• Mobile-first responsive design</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#6C4F77] mb-4">Content Strategy</h3>
                <ul className="space-y-2 text-[#2C2C2C]/80">
                  <li>• Focus on emotional benefits over features</li>
                  <li>• Use storytelling to illustrate scenarios</li>
                  <li>• Emphasize the human element</li>
                  <li>• Share relatable examples of forgotten moments</li>
                  <li>• Maintain empathetic, understanding tone</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <div className="text-center py-8">
          <p className="text-[#2C2C2C]/60 mb-4">
            This playbook should guide all marketing, product, and communication decisions while staying true to the beautiful wabi-sabi philosophy.
          </p>
          <div className="flex items-center justify-center gap-2 text-[#6C4F77] font-medium">
            <WabisabiIcon size={60} />
            <span>Made with love for deeper connections</span>
          </div>
        </div>
      </div>
      
      <FooterSection />
    </div>
  );
};

export default BrandPlaybook;
